import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowLeft } from 'lucide-react';
import { useDroplet } from '../context/DropletContext';
import { PasswordInput } from '../components';

const MIN_PASSWORD_LENGTH = 12;

export function ResetPassword() {
  const { authApi } = useDroplet();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const result = await authApi.resetPassword(token, newPassword);
      if (result.success) {
        setSuccess(true);
        setTimeout(() => navigate('/'), 2500);
      } else {
        setError(result.error || 'Invalid or expired reset link');
      }
    } finally {
      setSubmitting(false);
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="3pplea Console" className="h-12 w-auto mb-3" />
          <h1 className="text-xl font-bold text-white">3pplea Console</h1>
          <p className="text-sm text-dark-400 mt-1">Choose a new password</p>
        </div>

        <div className="bg-dark-900 border border-dark-700 rounded-xl p-6 space-y-4">
          {!token ? (
            <div className="text-center space-y-4">
              <div className="bg-red-900/30 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
                This reset link is missing its token. Request a new one below.
              </div>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
              >
                <ArrowLeft className="h-4 w-4" />
                Request a new reset link
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-2">
              <div className="bg-green-900/30 border border-green-700 text-green-300 text-sm rounded-lg px-3 py-2">
                Password updated. Redirecting you to sign in…
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-900/30 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-xs text-dark-400 mb-1">
                  New password
                </label>
                <PasswordInput
                  id="newPassword"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs text-dark-400 mb-1">
                  Confirm new password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2 text-sm transition-colors"
              >
                <KeyRound className="h-4 w-4" />
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
